import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flame, Battery, ThermometerSun, Sun } from "lucide-react";
import warmtepompImg from "@/assets/warmtepomp.jpg";
import zonneboilerImg from "@/assets/zonneboiler.jpg";
import thuisbatterijImg from "@/assets/thuisbatterij.jpg";
import { openOpenSolar } from "@/lib/widgets";
import { track } from "@/lib/analytics";

export const ThermalStorageSection = () => {
  return (
    <section
      id="warmte-opslag"
      aria-labelledby="thermal-storage-heading"
      className="py-16 md:py-24 bg-muted/30"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            <Flame className="h-3.5 w-3.5" /> Energie-advies
          </span>
          <h2
            id="thermal-storage-heading"
            className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-foreground"
          >
            Overtollige energie opslaan als warmte: slim alternatief voor batterijen
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            In plaats van stroom op te slaan in een thuisbatterij, kun je zonnestroom direct omzetten in warmte.
            Daarmee verhoog je je eigen verbruik en verlaag je de energierekening, zonder complexe accu-installaties.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <ThermometerSun className="h-5 w-5 text-primary" /> Warmteopslag: wanneer interessant?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Je hebt een elektrische boiler, warmtepomp(boiler) of vloerverwarming.</li>
                <li>Je wilt simpel en onderhoudsarm je eigen verbruik verhogen.</li>
                <li>Lagere investering dan een thuisbatterij, vaak sneller terugverdiend.</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Battery className="h-5 w-5 text-primary" /> Wanneer toch een thuisbatterij?
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <ul className="list-disc pl-5 space-y-2">
                <li>Je wil ook ’s avonds/nacht stroom gebruiken zonder netinvoer.</li>
                <li>Beperkte mogelijkheden om warmte op te slaan in het systeem.</li>
                <li>Netcongestie vermijden en peak-shaving zijn prioriteit.</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <article className="group">
            <img
              src={zonneboilerImg}
              alt="Zonneboiler zet zonnestroom om in warm water"
              loading="lazy"
              className="h-40 w-full rounded-md object-cover"
            />
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Sun className="h-5 w-5 text-primary" /> Zonneboiler / PVT
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Gebruik zonnewarmte voor sanitair warm water. Combineerbaar met warmtepomp en PV.
              </CardContent>
            </Card>
          </article>

          <article className="group">
            <img
              src={warmtepompImg}
              alt="Buffervat met warmtepomp voor warmteopslag"
              loading="lazy"
              className="h-40 w-full rounded-md object-cover"
            />
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <ThermometerSun className="h-5 w-5 text-primary" /> Buffervat + warmtepomp
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Sla warmte op in een buffervat en benut overdag opgewekte energie voor later comfort.
              </CardContent>
            </Card>
          </article>

          <article className="group">
            <img
              src={thuisbatterijImg}
              alt="Slimme boilersturing als alternatief voor thuisbatterij"
              loading="lazy"
              className="h-40 w-full rounded-md object-cover"
            />
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Flame className="h-5 w-5 text-primary" /> Slimme boilersturing
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Stuur je (warmtepomp)boiler aan op basis van zonneproductie en dynamische tarieven.
              </CardContent>
            </Card>
          </article>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button variant="cta" onClick={() => { track("cta_offerte_thermal"); openOpenSolar(); }} aria-label="Open OpenSolar offerteformulier">
            Offerte aanvragen
          </Button>
          <Button variant="outline" onClick={() => { track("cta_calendly_thermal"); (window as any).Calendly?.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}}); }} aria-label="Plan een gratis adviesgesprek">
            Plan adviesgesprek
          </Button>
          <Button asChild variant="secondary">
            <Link to="/warmtepompen" aria-label="Lees meer over warmtepompen">Meer over warmtepompen</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/zonneboilers" aria-label="Lees meer over zonneboilers">Zonneboilers</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
